/**
 * Confirmation token utilities
 * Generate and validate confirmation tokens for edit operations
 */

import crypto from 'crypto';
import type { ConfirmationTokenData } from '../types';

const TOKEN_EXPIRY_MS = 15 * 60 * 1000; // 15 minutes
const SECRET_KEY = process.env.TOOL_CONFIRMATION_SECRET || 'default-secret-key';

/**
 * Generate a confirmation token
 */
export function generateConfirmationToken(data: Omit<ConfirmationTokenData, 'createdAt' | 'expiresAt'>): string {
  const now = Date.now();
  const tokenData: ConfirmationTokenData = {
    ...data,
    createdAt: now,
    expiresAt: now + TOKEN_EXPIRY_MS,
  };

  const payload = JSON.stringify(tokenData);
  const hmac = crypto.createHmac('sha256', SECRET_KEY);
  hmac.update(payload);
  const signature = hmac.digest('hex');

  // Combine payload and signature
  const combined = `${Buffer.from(payload).toString('base64')}.${signature}`;
  return combined;
}

/**
 * Validate and decode a confirmation token
 */
export function validateConfirmationToken(token: string): ConfirmationTokenData | null {
  try {
    const [encodedPayload, signature] = token.split('.');

    if (!encodedPayload || !signature) {
      return null;
    }

    const payload = Buffer.from(encodedPayload, 'base64').toString('utf-8');
    const hmac = crypto.createHmac('sha256', SECRET_KEY);
    hmac.update(payload);
    const expectedSignature = hmac.digest('hex');

    // Verify signature
    if (signature !== expectedSignature) {
      return null;
    }

    const data: ConfirmationTokenData = JSON.parse(payload);

    // Check expiration
    if (data.expiresAt < Date.now()) {
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const data = validateConfirmationToken(token);
  return data === null;
}
